'use client';

import {
  ButtonHTMLAttributes,
  ReactElement,
  forwardRef,
  ForwardedRef,
  useState,
} from 'react';
import classNames from 'classnames';
import './css/Chip.scss';
import { CheckSvg } from './CheckSvg';
import { CloseSvg } from './CloseSvg';

export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isSelected?: boolean;
  hasClose?: boolean;
  closeButtonAriaLabel?: string;
  icon?: ReactElement;
  onClose?: () => void;
}

export const Chip = forwardRef(
  (props: ChipProps, ref: ForwardedRef<HTMLButtonElement>) => {
    const {
      children,
      className,
      disabled,
      hasClose,
      closeButtonAriaLabel = 'Remove',
      icon,
      isSelected: isSelectedProp = false,
      onClose,
      ...spreadProps
    } = props;

    const [isClosed, toggleClose] = useState(false);
    const [isSelected, toggleSelection] = useState(isSelectedProp);

    const handleClick = () => {
      if (hasClose) {
        toggleClose(!isClosed);
        onClose?.();
      }

      if (!hasClose) {
        toggleSelection(!isSelected);
      }
    };

    const chipClasses = classNames({
      'ps-chip': true,
      'ps-chip-is-selected': isSelected,
      'ps-chip-has-close': hasClose,
      'ps-chip-is-closed': isClosed,
      [`${className}`]: className,
    });

    return (
      <button
        {...spreadProps}
        className={chipClasses}
        disabled={disabled}
        type="button"
        ref={ref}
        onClick={handleClick}
        aria-pressed={!hasClose ? isSelected : undefined}
      >
        <CloseSvg ariaLabel={closeButtonAriaLabel} />
        {icon || <CheckSvg />}
        <span className="ps-chip-label">{children}</span>
      </button>
    );
  }
);
